import { useEffect, useState, useRef } from 'react';
import { Box, Spinner, Center, Text, VStack, HStack, Icon } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FiCheck, FiLoader, FiFileText, FiImage } from 'react-icons/fi';
import { getWebsiteSubdomain, getFullDomainForLookup } from 'src/lib/utils/subdomain';
import { fetchPublicWebsite } from 'src/services/publicWebsite';
import type { TrackingConfig } from 'src/services/publicWebsite';

interface WebsiteData {
	html: string;
	css: string;
	javascript: string;
	tracking?: TrackingConfig;
	accountName: string;
	domain: string;
	websiteStep: number;
	meta?: {
		title?: string;
		description?: string;
		keywords?: string;
	};
}

// Pulse animation for the current step
const pulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.5; }
`;

// Spin animation for the loading icon
const spin = keyframes`
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
`;

interface GenerationStepProps {
	stepNumber: number;
	title: string;
	description: string;
	icon: React.ElementType;
	isActive: boolean;
	isComplete: boolean;
}

const GenerationStep = ({ stepNumber, title, description, icon, isActive, isComplete }: GenerationStepProps) => {
	return (
		<HStack
			spacing={4}
			p={4}
			bg={isActive ? 'blue.50' : isComplete ? 'green.50' : 'gray.50'}
			borderRadius="lg"
			border="1px solid"
			borderColor={isActive ? 'blue.200' : isComplete ? 'green.200' : 'gray.200'}
			w="100%"
			animation={isActive ? `${pulse} 2s infinite` : undefined}
		>
			<Box
				p={3}
				borderRadius="full"
				bg={isComplete ? 'green.500' : isActive ? 'blue.500' : 'gray.300'}
				color="white"
			>
				{isActive ? (
					<Icon as={FiLoader} boxSize={5} animation={`${spin} 1s linear infinite`} />
				) : isComplete ? (
					<Icon as={FiCheck} boxSize={5} />
				) : (
					<Icon as={icon} boxSize={5} />
				)}
			</Box>
			<VStack align="start" spacing={0} flex={1}>
				<HStack>
					<Text fontWeight="semibold" color={isActive ? 'blue.700' : isComplete ? 'green.700' : 'gray.600'}>
						Step {stepNumber}: {title}
					</Text>
					{isComplete && (
						<Text fontSize="sm" color="green.600" fontWeight="medium">
							✓ Complete
						</Text>
					)}
					{isActive && (
						<Text fontSize="sm" color="blue.600" fontWeight="medium">
							In progress...
						</Text>
					)}
				</HStack>
				<Text fontSize="sm" color="gray.500">
					{description}
				</Text>
			</VStack>
		</HStack>
	);
};

interface GenerationStatusBannerProps {
	websiteStep: number;
	accountName: string;
}

const GenerationStatusBanner = ({ websiteStep, accountName }: GenerationStatusBannerProps) => {
	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			right={0}
			zIndex={1000}
			bg="white"
			borderBottom="1px solid"
			borderColor="gray.200"
			boxShadow="sm"
		>
			<Box maxW="600px" mx="auto" p={6}>
				<VStack spacing={4} align="stretch">
					<VStack spacing={1} align="center" mb={2}>
						<Text fontSize="xl" fontWeight="bold" color="gray.800">
							Building Your Website
						</Text>
						<Text fontSize="sm" color="gray.500">
							{accountName ? `${accountName} - ` : ''}We&apos;re creating something amazing for you!
						</Text>
					</VStack>

					<GenerationStep
						stepNumber={1}
						title="Generating Content"
						description="Creating text, structure, and page schema"
						icon={FiFileText}
						isActive={websiteStep === 0}
						isComplete={websiteStep >= 1}
					/>

					<GenerationStep
						stepNumber={2}
						title="Generating Images"
						description="Creating custom images and visuals"
						icon={FiImage}
						isActive={websiteStep === 1}
						isComplete={websiteStep >= 2}
					/>

					<Text fontSize="xs" color="gray.400" textAlign="center" mt={2}>
						This usually takes a few minutes. The page will update automatically when ready.
					</Text>
				</VStack>
			</Box>
		</Box>
	);
};

const PublicWebsite = () => {
	const [websiteData, setWebsiteData] = useState<WebsiteData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const containerRef = useRef<HTMLDivElement>(null);
	const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

	const loadWebsite = async () => {
		const subdomain = getWebsiteSubdomain();
		const fullDomain = getFullDomainForLookup();

		if (!subdomain || !fullDomain) {
			setError('Invalid website');
			setLoading(false);
			return;
		}

		try {
			console.log('[PublicWebsite] Loading website for domain:', fullDomain);
			
			const data = await fetchPublicWebsite(fullDomain);
			
			console.log('[PublicWebsite] Website loaded successfully:', {
				accountName: data.accountName,
				domain: data.domain,
				htmlLength: data.html?.length,
				cssLength: data.css?.length,
				jsLength: data.javascript?.length,
				websiteStep: data.websiteStep,
			});

			setWebsiteData(data);

			// Update page title from meta or accountName
			const pageTitle = data.meta?.title || data.accountName;
			if (pageTitle) {
				document.title = pageTitle;
			}
			
			// Update meta description
			if (data.meta?.description) {
				const metaDesc = document.querySelector('meta[name="description"]');
				if (metaDesc) {
					metaDesc.setAttribute('content', data.meta.description);
				}
			}

			// Remove default FusionAds.ai favicon
			const existingFavicon = document.querySelector('link[rel="icon"]');
			if (existingFavicon) {
				existingFavicon.remove();
			}
			
			// Hide any Intercom elements
			const intercomElements = document.querySelectorAll('[id^="intercom"], .intercom-lightweight-app, .intercom-app');
			intercomElements.forEach(el => {
				(el as HTMLElement).style.display = 'none';
			});
			
			// Shutdown Intercom if it exists
			if ((window as any).Intercom) {
				(window as any).Intercom('shutdown');
			}

			// If website is complete, stop polling
			if (data.websiteStep >= 2) {
				if (pollIntervalRef.current) {
					clearInterval(pollIntervalRef.current);
					pollIntervalRef.current = null;
				}
			}
		} catch (err: any) {
			console.error('[PublicWebsite] Error loading website:', err);
			
			if (err.message?.includes('Account not found') || err.message?.includes('404')) {
				setError('Website not found');
			} else if (err.message?.includes('No pages')) {
				setError('This website has no content yet');
			} else {
				setError('Failed to load website');
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadWebsite();

		// Set up polling if website is not complete
		// Poll every 10 seconds to check for updates
		pollIntervalRef.current = setInterval(() => {
			if (websiteData && websiteData.websiteStep < 2) {
				console.log('[PublicWebsite] Polling for updates...');
				loadWebsite();
			}
		}, 10000);

		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
			}
		};
	}, []);

	// Re-check polling when websiteStep changes
	useEffect(() => {
		if (websiteData && websiteData.websiteStep >= 2 && pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
	}, [websiteData]);

	// Inject tracking scripts into <head> as static-like elements.
	// Google's phone swap (GFN) requires gtag to be in <head>, not dynamically in <body>.
	useEffect(() => {
		const tracking = websiteData?.tracking;
		if (!tracking) return;

		const cleanupIds: string[] = [];

		// 1. Load gtag.js — skip if already loaded by the early loader in index.html
		const earlyGtagLoaded = !!document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
		if (tracking.gtag_id && !earlyGtagLoaded) {
			const gtagSrc = document.createElement('script');
			gtagSrc.id = 'public-website-gtag-src';
			gtagSrc.async = true;
			gtagSrc.src = `https://www.googletagmanager.com/gtag/js?id=${tracking.gtag_id}`;
			document.head.appendChild(gtagSrc);
			cleanupIds.push(gtagSrc.id);

			// 2. Inline config script (must be AFTER gtag.js src tag, like a static page)
			const gtagConfig = document.createElement('script');
			gtagConfig.id = 'public-website-gtag-config';
			gtagConfig.textContent = [
				'window.dataLayer=window.dataLayer||[];',
				'function gtag(){dataLayer.push(arguments);}',
				"gtag('js',new Date());",
				`gtag('config','${tracking.gtag_id}');`,
				tracking.gtag_label && tracking.phone
					? `gtag('config','${tracking.gtag_id}/${tracking.gtag_label}',{'phone_conversion_number':'${tracking.phone}'});`
					: '',
			].filter(Boolean).join('\n');
			document.head.appendChild(gtagConfig);
			cleanupIds.push(gtagConfig.id);
		}

		// 3. GTM container
		if (tracking.gtm_id) {
			const gtmScript = document.createElement('script');
			gtmScript.id = 'public-website-gtm';
			gtmScript.textContent =
				`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':` +
				`new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
				`j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
				`'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
				`})(window,document,'script','dataLayer','${tracking.gtm_id}');`;
			document.head.appendChild(gtmScript);
			cleanupIds.push(gtmScript.id);
		}

		// 4. Facebook Pixel
		if (tracking.fb_pixel_id) {
			const fbScript = document.createElement('script');
			fbScript.id = 'public-website-fb-pixel';
			fbScript.textContent =
				`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?` +
				`n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;` +
				`n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
				`t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}` +
				`(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
				`fbq('init','${tracking.fb_pixel_id}');fbq('track','PageView');`;
			document.head.appendChild(fbScript);
			cleanupIds.push(fbScript.id);
		}

		console.log('[PublicWebsite] Tracking scripts injected into <head>:', Object.keys(tracking));

		return () => {
			cleanupIds.forEach(id => {
				const el = document.getElementById(id);
				if (el) el.remove();
			});
		};
	}, [websiteData?.tracking]);

	// Inject CSS into the page head
	useEffect(() => {
		if (websiteData?.css) {
			// Remove any previously injected styles
			const existingStyle = document.getElementById('public-website-styles');
			if (existingStyle) {
				existingStyle.remove();
			}
			
			// Create and inject new style element
			const styleElement = document.createElement('style');
			styleElement.id = 'public-website-styles';
			styleElement.textContent = websiteData.css;
			document.head.appendChild(styleElement);
			
			console.log('[PublicWebsite] CSS injected:', websiteData.css.length, 'chars');
		}
		
		return () => {
			// Cleanup on unmount
			const styleElement = document.getElementById('public-website-styles');
			if (styleElement) {
				styleElement.remove();
			}
		};
	}, [websiteData?.css]);

	// Inject the HTML into the container and handle head elements (favicon, meta, etc.)
	useEffect(() => {
		if (websiteData?.html && containerRef.current) {
			// Clear any existing content
			containerRef.current.innerHTML = '';
			
			// Create a temporary container to parse the HTML
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = websiteData.html;
			
			// Extract and apply head elements (meta tags, link tags, title, etc.)
			const headElements = tempDiv.querySelectorAll('meta, link, title, style');
			headElements.forEach((el) => {
				const tagName = el.tagName.toLowerCase();
				
				if (tagName === 'title') {
					document.title = el.textContent || websiteData.accountName || 'Website';
				} else if (tagName === 'link') {
					const linkEl = el as HTMLLinkElement;
					const rel = linkEl.getAttribute('rel');
					
					// Handle favicon specifically
					if (rel === 'icon' || rel === 'shortcut icon') {
						// Remove ALL existing favicons first
						const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
						existingFavicons.forEach(f => f.remove());
						
						// Create new favicon link in head
						const newFavicon = document.createElement('link');
						newFavicon.rel = 'icon';
						newFavicon.href = linkEl.href;
						if (linkEl.type) newFavicon.type = linkEl.type;
						document.head.appendChild(newFavicon);
					} else if (rel === 'stylesheet') {
						// Move stylesheets to head
						const newLink = document.createElement('link');
						Array.from(linkEl.attributes).forEach(attr => {
							newLink.setAttribute(attr.name, attr.value);
						});
						document.head.appendChild(newLink);
					}
					// Remove from body content
					el.remove();
				} else if (tagName === 'meta') {
					const metaEl = el as HTMLMetaElement;
					const name = metaEl.getAttribute('name');
					const property = metaEl.getAttribute('property');
					
					// Update or create meta tags
					if (name || property) {
						const selector = name 
							? `meta[name="${name}"]` 
							: `meta[property="${property}"]`;
						const existing = document.head.querySelector(selector);
						if (existing) {
							existing.setAttribute('content', metaEl.content);
						} else {
							const newMeta = document.createElement('meta');
							Array.from(metaEl.attributes).forEach(attr => {
								newMeta.setAttribute(attr.name, attr.value);
							});
							document.head.appendChild(newMeta);
						}
					}
					// Remove from body content
					el.remove();
				} else if (tagName === 'style') {
					// Move inline styles to head
					const newStyle = document.createElement('style');
					newStyle.textContent = el.textContent;
					document.head.appendChild(newStyle);
					// Remove from body content
					el.remove();
				}
			});
			
			// Move all child nodes to the container
			while (tempDiv.firstChild) {
				containerRef.current.appendChild(tempDiv.firstChild);
			}
			
			console.log('[PublicWebsite] HTML injected');
		}
	}, [websiteData?.html]);

	// Inject JavaScript after HTML is in place
	useEffect(() => {
		if (websiteData?.javascript && containerRef.current) {
			// Remove any previously injected scripts
			const existingScript = document.getElementById('public-website-script');
			if (existingScript) {
				existingScript.remove();
			}
			
			// Create and inject new script element
			const scriptElement = document.createElement('script');
			scriptElement.id = 'public-website-script';
			scriptElement.textContent = websiteData.javascript;
			document.body.appendChild(scriptElement);
			
			console.log('[PublicWebsite] JavaScript injected:', websiteData.javascript.length, 'chars');
		}
		
		return () => {
			// Cleanup on unmount
			const scriptElement = document.getElementById('public-website-script');
			if (scriptElement) {
				scriptElement.remove();
			}
		};
	}, [websiteData?.javascript, websiteData?.html]);

	if (loading) {
		return (
			<Center h="100vh" bg="white">
				<Spinner size="xl" color="blue.500" thickness="4px" />
			</Center>
		);
	}

	if (error) {
		return (
			<Center h="100vh" flexDirection="column" bg="white">
				<Text fontSize="2xl" fontWeight="bold" mb={4} color="gray.800">
					{error}
				</Text>
				<Text color="gray.500">
					The website you&apos;re looking for doesn&apos;t exist or is unavailable.
				</Text>
			</Center>
		);
	}

	// Show generation status banner if website is not complete
	const showBanner = websiteData && websiteData.websiteStep < 2;
	const bannerHeight = showBanner ? '280px' : '0px';

	if (!websiteData?.html && showBanner) {
		// No HTML yet, just show the banner
		return (
			<Box minH="100vh" bg="gray.50">
				<GenerationStatusBanner 
					websiteStep={websiteData.websiteStep} 
					accountName={websiteData.accountName} 
				/>
				<Center h="100vh" flexDirection="column">
					<Text fontSize="lg" color="gray.500" mt="300px">
						Your website content will appear here shortly...
					</Text>
				</Center>
			</Box>
		);
	}

	if (!websiteData?.html) {
		return (
			<Center h="100vh" flexDirection="column" bg="white">
				<Text fontSize="2xl" fontWeight="bold" mb={4} color="gray.800">
					No content available
				</Text>
			</Center>
		);
	}

	// Render the website HTML with optional banner
	return (
		<Box minH="100vh" w="100%">
			{showBanner && (
				<GenerationStatusBanner 
					websiteStep={websiteData.websiteStep} 
					accountName={websiteData.accountName} 
				/>
			)}
			<Box
				ref={containerRef}
				id="public-website-container"
				minH="100vh"
				w="100%"
				pt={bannerHeight}
			/>
		</Box>
	);
};

export default PublicWebsite;
